import { createRouter, createWebHashHistory } from "vue-router";
import Swal from "sweetalert2";
import AdminLayout from "../layouts/AdminLayout.vue";
import UserLayout from "../layouts/UserLayout.vue";
import UserFormLayout from "@/layouts/UserFormLayout.vue";
import TherapistLayout from "../layouts/TherapistLayout.vue";
import TherapistsPage from "../pages/admin/TherapistsPage.vue";
import TherapistDetailsPage from "../pages/admin/TherapistDetailsPage.vue";
import CalendarPage from "../pages/admin/CalendarPage.vue";
import MassActions from "@/pages/admin/MassActions.vue";
import DashboardPage from "@/pages/admin/DashboardPage.vue";
import SyncHealth from "@/pages/admin/SyncHealth.vue";
import { isCurrentUserAuthenticated, isCurrentUserRoleIsAdmin } from "@/lib/auth";

const BACKEND_TARGET = "current"; // "current" | "dev" | "prod"
const DEV_BACKEND_URL = "https://calendar-877375085.development.catalystserverless.com/";
const PROD_BACKEND_URL = "https://calendar-877375085.catalystserverless.com/";

function getCalendarUrl() {
    if (BACKEND_TARGET === "dev") return DEV_BACKEND_URL;
    if (BACKEND_TARGET === "prod") return PROD_BACKEND_URL;

    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1") return "http://localhost:3000/";
    if (hostname.includes(".development.")) return DEV_BACKEND_URL;
    return PROD_BACKEND_URL;
}

const calendar_url = getCalendarUrl();

if (calendar_url.includes(".development.")) console.warn("Using development backend:", calendar_url);
if (calendar_url.includes("localhost")) console.info("Using LOCALHOST backend:", calendar_url);

const routes = [
    {
        path: "/",
        component: UserLayout,
        children: [
            {
                path: "",
                name: "home",
                component: () => import("@/pages/user/LandingPage.vue"),
                meta: { requiresAuth: false }
            },
            {
                path: "login",
                name: "login",
                component: {
                    render: function render() {
                        return null;
                    },
                    // Instead of immediate redirect, handle it properly
                    beforeRouteEnter: function beforeRouteEnter() {
                        // We're in an iframe, open in parent window
                        if (window.top !== window.self) window.top.location.href = `${calendar_url}__catalyst/auth/login`;
                        else window.location.href = `${calendar_url}__catalyst/auth/login`;
                    }
                },
                meta: { requiresAuth: false }
            },
            {
                path: "logout",
                name: "logout",
                component: {
                    render: function render() {
                        return null;
                    },
                    beforeRouteEnter: async function beforeRouteEnter() {
                        // Create loader
                        Swal.fire({
                            title: "Logging out...",
                            text: "Please wait.",
                            allowOutsideClick: false,
                            didOpen: () => {
                                Swal.showLoading();
                                window.catalyst.auth.signOut(`${calendar_url}`);
                            }
                        });
                    }
                }
            }
        ]
    },
    {
        path: "/form",
        component: UserFormLayout,
        children: [
            {
                path: "book",
                name: "booking-form",
                component: () => import("@/pages/user/forms/BookingForm.vue")
            },
            {
                path: "browse",
                name: "booking-browse",
                component: () => import("@/pages/user/forms/BookingBrowse.vue")
            }
        ]
    },
    {
        path: "/therapist",
        component: TherapistLayout,
        meta: { requiresAuth: true },
        children: [
            // Therapist routes
        ]
    },
    {
        path: "/admin",
        component: AdminLayout,
        meta: { requiresAuth: true, requiresAdmin: true },
        children: [
            {
                path: "dashboard",
                component: DashboardPage,
                meta: { title: "Dashboard", requiresAuth: true, requiresAdmin: true }
            },
            {
                path: "",
                redirect: "/admin/therapists"
            },
            {
                path: "therapists",
                name: "therapists",
                component: TherapistsPage,
                meta: { title: "Therapists", requiresAuth: true, requiresAdmin: true }
            },
            {
                path: "therapist/:therapistId",
                name: "therapist-details",
                component: TherapistDetailsPage,
                props: true,
                meta: { title: "Therapist Details", requiresAuth: true, requiresAdmin: true }
            },
            {
                path: "therapist/:therapistId/calendar/:locationId",
                name: "therapist-calendar",
                component: CalendarPage,
                props: true,
                meta: { title: "Therapist Calendar", requiresAuth: true, requiresAdmin: true }
            },
            {
                path: "mass_actions",
                name: "mass-actions",
                component: MassActions,
                props: true,
                meta: { title: "Mass Actions", requiresAuth: true, requiresAdmin: true }
            },
            {
                path: "sync_health",
                name: "sync-health",
                component: SyncHealth,
                props: true,
                meta: { title: "Sync Health", requiresAuth: true, requiresAdmin: true }
            }
        ]
    },
    {
        path: "/:pathMatch(.*)*",
        redirect: "/"
    }
];

export const router = createRouter({
    history: createWebHashHistory(),
    routes: routes
});

// Navigation guard
// eslint-disable-next-line consistent-return
router.beforeEach(async (to, from, next) => {
    // Check if route requires authentication
    if (to.matched.some((record) => record.meta.requiresAuth)) {
        const isAuthenticated = await isCurrentUserAuthenticated();

        if (!isAuthenticated) {
            // Redirect to login page if not authenticated
            return next({ name: "login" });
        }

        // Check if route requires admin permissions
        if (to.matched.some((record) => record.meta.requiresAdmin)) {
            const isAdmin = await isCurrentUserRoleIsAdmin();

            if (!isAdmin) {
                // Show error and redirect to home page
                Swal.fire({
                    title: "Access Denied",
                    text: "You don't have administrator permissions to access this page.",
                    icon: "error",
                    confirmButtonText: "OK"
                });
                return next({ name: "home" });
            }
        }
    }

    next();
});
